import {useCallback} from 'react';
import {useAnalytics} from '@common-web/hooks/useAnalytics';
import type {AnalyticsProperties} from '@common-web/lib/analytics';
import type {CoverageTier, ProductCategory} from '@client/pages/public/enroll/types';
import type {EnrollStep} from '@client/pages/public/enroll/enrollModel';

export const EnrollEvent = {
  LandingViewed: 'enroll_landing_viewed',
  AccessCodeSubmitted: 'enroll_access_code_submitted',
  StepViewed: 'enroll_step_viewed',
  PlanSelected: 'enroll_plan_selected',
  PlanDeclined: 'enroll_plan_declined',
  CheckoutViewed: 'enroll_checkout_viewed',
  SubmissionSucceeded: 'enroll_submission_succeeded',
  SubmissionFailed: 'enroll_submission_failed',
  AppDownloadClicked: 'enroll_app_download_clicked',
} as const;

type EnrollEvents = {
  [EnrollEvent.LandingViewed]: Record<string, never>;
  [EnrollEvent.AccessCodeSubmitted]: {success: boolean};
  [EnrollEvent.StepViewed]: {step: EnrollStep};
  [EnrollEvent.PlanSelected]: {category: ProductCategory; plan_id: string; tier: CoverageTier};
  [EnrollEvent.PlanDeclined]: {category: ProductCategory};
  [EnrollEvent.CheckoutViewed]: Record<string, never>;
  [EnrollEvent.SubmissionSucceeded]: {
    plan_count: number;
    declined_count: number;
    dependent_count: number;
  };
  [EnrollEvent.SubmissionFailed]: {reason: 'access_expired' | 'already_enrolled' | 'error'};
  [EnrollEvent.AppDownloadClicked]: {
    store: 'app_store' | 'play_store';
    surface: 'banner' | 'modal';
  };
};

export function useEnrollAnalytics() {
  const {track} = useAnalytics();

  const trackEnroll = useCallback(
    <E extends keyof EnrollEvents>(event: E, properties?: EnrollEvents[E]) => {
      track(event, properties as AnalyticsProperties | undefined);
    },
    [track],
  );

  return {track: trackEnroll};
}
